import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Activity, CheckCircle2, AlertTriangle, AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  healthy: {
    Icon: CheckCircle2,
    ring: 'border-emerald-500/30 bg-emerald-500/5',
    text: 'text-emerald-300',
    dot: 'bg-emerald-400',
    label: 'Healthy',
  },
  warning: {
    Icon: AlertTriangle,
    ring: 'border-amber-500/30 bg-amber-500/5',
    text: 'text-amber-300',
    dot: 'bg-amber-400',
    label: 'Warning',
  },
  critical: {
    Icon: AlertCircle,
    ring: 'border-rose-500/30 bg-rose-500/5',
    text: 'text-rose-300',
    dot: 'bg-rose-400',
    label: 'Critical',
  },
};

const barColor = (count, max) => {
  if (count === 0) return 'bg-white/5';
  const pct = max ? count / max : 0;
  if (pct >= 0.8) return 'bg-emerald-400';
  if (pct >= 0.5) return 'bg-teal-400';
  if (pct >= 0.25) return 'bg-teal-500/70';
  return 'bg-teal-500/40';
};

const Thermometer = ({ buckets }) => {
  const max = Math.max(...buckets.map(b => b.count), 1);
  return (
    <div className="flex items-end gap-1 h-12 shrink-0" data-testid="pipeline-thermometer">
      {buckets.map((b) => {
        const heightPct = b.count === 0 ? 6 : Math.max(12, (b.count / max) * 100);
        const labelHour = b.hour_offset === 0 ? 'now' : `${b.hour_offset}h`;
        return (
          <div
            key={b.hour_offset}
            className="flex flex-col items-center gap-0.5 w-5"
            title={`${labelHour} · ${b.count} call${b.count === 1 ? '' : 's'}`}
          >
            <div
              className={`w-full ${barColor(b.count, max)} rounded-sm transition-all`}
              style={{ height: `${heightPct}%` }}
            />
          </div>
        );
      })}
    </div>
  );
};

export const PipelineHealth = ({ domainId, token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resuming, setResuming] = useState(false);

  const load = () => axios.get(`${API}/admin/domains/${domainId}/pipeline-health`, {
    headers: { Authorization: `Bearer ${token}` },
  }).then(r => r.data);

  useEffect(() => {
    let cancelled = false;
    const run = () => {
      load()
        .then(d => { if (!cancelled) { setData(d); setLoading(false); } })
        .catch(() => { if (!cancelled) setLoading(false); });
    };
    run();
    const iv = setInterval(run, 60000);
    return () => { cancelled = true; clearInterval(iv); };
     
  }, [domainId, token]);

  const handleResumeSync = async () => {
    setResuming(true);
    try {
      await axios.post(`${API}/admin/domains/${domainId}/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Sync triggered — give it ~30s, then check back.');
      // Refresh health after a short delay
      setTimeout(() => load().then(setData).catch(() => null), 5000);
    } catch (e) {
      toast.error('Failed to trigger sync. Check Yabbit API credentials.');
      console.error('Resume sync failed:', e);
    } finally {
      setResuming(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-5 py-3">
        <Loader2 className="w-4 h-4 animate-spin text-white/40" />
        <span className="text-white/40 text-sm">Checking pipeline health…</span>
      </div>
    );
  }
  if (!data) return null;

  const cfg = STATUS_STYLE[data.status] || STATUS_STYLE.warning;
  const { Icon } = cfg;
  const lastCallLabel = data.hours_since_last_call == null
    ? 'never'
    : data.hours_since_last_call < 1
      ? `${Math.round(data.hours_since_last_call * 60)}m ago`
      : `${data.hours_since_last_call}h ago`;

  return (
    <div
      className={`flex items-center gap-4 border rounded-xl px-5 py-3 ${cfg.ring}`}
      data-testid="pipeline-health-widget"
    >
      <div className="flex items-center gap-2 shrink-0">
        <Icon className={`w-4 h-4 ${cfg.text}`} />
        <span className={`text-xs font-bold uppercase tracking-wider ${cfg.text}`} data-testid="pipeline-health-status">
          Pipeline · {cfg.label}
        </span>
        <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot} animate-pulse`} />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-white/80 text-sm truncate">{data.reason}</p>
        <p className="text-white/40 text-xs">
          <span data-testid="pipeline-calls-12h" className="text-white/70 font-semibold">{data.calls_last_12h}</span> calls in 12h
          {data.expected_12h > 0 && <span className="text-white/30"> · avg {data.expected_12h}</span>}
          <span className="text-white/30"> · last call {lastCallLabel}</span>
        </p>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {data.status === 'critical' && (
          <button
            onClick={handleResumeSync}
            disabled={resuming}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-rose-500/20 hover:bg-rose-500/30 border border-rose-500/30 text-rose-200 rounded-lg disabled:opacity-50 transition-colors"
            data-testid="pipeline-resume-sync-btn"
          >
            {resuming ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            {resuming ? 'Triggering…' : 'Resume Sync'}
          </button>
        )}
        <div className="text-right hidden sm:block">
          <p className="text-white/30 text-[10px] uppercase tracking-wide">12h</p>
          <p className="text-white/50 text-[10px]">now</p>
        </div>
        <Thermometer buckets={data.hourly_buckets} />
        <Activity className="w-3.5 h-3.5 text-white/20 ml-1" />
      </div>
    </div>
  );
};
